import { useState, useEffect, useCallback } from "react";

import axios from "axios";

import { useOrgSession } from "@domains/auth/hooks/useOrgSession";
import { orgSessionManager } from "@domains/auth/services/orgSessionManager";
import { apiBaseUrls } from "@platform/constants/shared-constants";
import { useRegistryCache } from "@platform/hooks/useRegistryCache";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface PipelineRunRow {
  id: string;
  sagaId: string;
  systemId: number;
  systemAlias: string;
  organizationId: number;
  intent: string;
  status: "queued" | "in_progress" | "completed" | "failed";
  sha: string | null;
  branch: string | null;
  commitMessage: string | null;
  failedAt: string | null;
  failureMessage: string | null;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
  /** 1-based attempt number within a logical run (P1-021 in-run retry). */
  attempt?: number;
}

export const PIPELINE_RUN_LIMIT_OPTIONS = [10, 20, 50, 100] as const;
export const DEFAULT_PIPELINE_RUN_LIMIT = 20;

export interface UsePipelineRunsApiResult {
  runs: PipelineRunRow[];
  total: number;
  limit: number;
  offset: number;
  hasNext: boolean;
  hasPrevious: boolean;
  loading: boolean;
  error: string | null;
  setLimit: (limit: number) => void;
  nextPage: () => void;
  previousPage: () => void;
  refetch: () => Promise<void>;
}

// ── Hook ───────────────────────────────────────────────────────────────────────

/**
 * Fetches pipeline runs from the REST API and merges with WebSocket-driven
 * pipelineState updates for real-time status changes.
 *
 * The REST endpoint returns the authoritative list of runs (including "queued"
 * runs that haven't received any events yet). The WebSocket updates flow through
 * PipelineStep events and update the run status in real-time.
 */
export function usePipelineRunsApi(
  alias: string | undefined,
): UsePipelineRunsApiResult {
  const initialPagination = readPipelinePagination();
  const [runs, setRuns] = useState<PipelineRunRow[]>([]);
  const [total, setTotal] = useState(0);
  const [limit, setLimitState] = useState(initialPagination.limit);
  const [offset, setOffset] = useState(initialPagination.offset);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { orgId } = useOrgSession();
  const { get: getCached, invalidateByPrefix } =
    useRegistryCache("application");
  const sessionOrgId = orgId ?? orgSessionManager.getOrgId();

  const fetchRuns = useCallback(
    async (force = false) => {
      if (!alias || sessionOrgId === null) return;
      const cacheKey = `pipelineRuns:${sessionOrgId}:${alias}:limit=${limit}:offset=${offset}`;
      if (force) {
        invalidateByPrefix(`pipelineRuns:${sessionOrgId}:${alias}:`);
      }

      setLoading(true);
      setError(null);
      try {
        const response = await getCached(cacheKey, async () => {
          const res = await axios.get<{
            runs: PipelineRunRow[];
            total: number;
            limit: number;
            offset: number;
            hasNext: boolean;
            hasPrevious: boolean;
          }>(
            `${apiBaseUrls.BACKEND}/api/v1/applications/${alias}/pipeline-runs`,
            {
              params: { limit, offset },
              withCredentials: true,
            },
          );
          return res.data;
        });
        setRuns(response.runs);
        setTotal(response.total);
        setHasNext(response.hasNext);
        setHasPrevious(response.hasPrevious);
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 403) {
          setError("Organization mismatch");
        } else {
          setError("Failed to fetch pipeline runs");
        }
        setRuns([]);
        setTotal(0);
        setHasNext(false);
        setHasPrevious(false);
      } finally {
        setLoading(false);
      }
    },
    [alias, getCached, invalidateByPrefix, limit, offset, sessionOrgId],
  );

  useEffect(() => {
    void fetchRuns();
  }, [fetchRuns]);

  useEffect(() => {
    const handlePopState = () => {
      const next = readPipelinePagination();
      setLimitState(next.limit);
      setOffset(next.offset);
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const setLimit = useCallback((nextLimit: number) => {
    if (
      !PIPELINE_RUN_LIMIT_OPTIONS.includes(
        nextLimit as (typeof PIPELINE_RUN_LIMIT_OPTIONS)[number],
      )
    )
      return;
    setLimitState(nextLimit);
    setOffset(0);
    updatePipelinePaginationUrl(nextLimit, 1);
  }, []);

  const nextPage = useCallback(() => {
    if (!hasNext) return;
    setOffset((current) => current + limit);
    updatePipelinePaginationUrl(limit, Math.floor(offset / limit) + 2);
  }, [hasNext, limit, offset]);

  const previousPage = useCallback(() => {
    if (!hasPrevious) return;
    setOffset((current) => Math.max(0, current - limit));
    updatePipelinePaginationUrl(limit, Math.max(1, Math.floor(offset / limit)));
  }, [hasPrevious, limit, offset]);

  const refetch = useCallback(() => fetchRuns(true), [fetchRuns]);

  return {
    runs,
    total,
    limit,
    offset,
    hasNext,
    hasPrevious,
    loading,
    error,
    setLimit,
    nextPage,
    previousPage,
    refetch,
  };
}

const PIPELINE_RUN_PAGE_PARAM = "run_page";
const PIPELINE_RUN_LIMIT_PARAM = "run_limit";

function readPipelinePagination(): { limit: number; offset: number } {
  if (typeof window === "undefined") {
    return { limit: DEFAULT_PIPELINE_RUN_LIMIT, offset: 0 };
  }

  const params = new URLSearchParams(window.location.search);
  const requestedLimit = Number(params.get(PIPELINE_RUN_LIMIT_PARAM));
  const limit = PIPELINE_RUN_LIMIT_OPTIONS.includes(
    requestedLimit as (typeof PIPELINE_RUN_LIMIT_OPTIONS)[number],
  )
    ? requestedLimit
    : DEFAULT_PIPELINE_RUN_LIMIT;
  const requestedPage = Number(params.get(PIPELINE_RUN_PAGE_PARAM));
  const page =
    Number.isInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1;
  return { limit, offset: (page - 1) * limit };
}

function updatePipelinePaginationUrl(limit: number, page: number): void {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);
  if (limit === DEFAULT_PIPELINE_RUN_LIMIT) {
    params.delete(PIPELINE_RUN_LIMIT_PARAM);
  } else {
    params.set(PIPELINE_RUN_LIMIT_PARAM, String(limit));
  }
  if (page === 1) {
    params.delete(PIPELINE_RUN_PAGE_PARAM);
  } else {
    params.set(PIPELINE_RUN_PAGE_PARAM, String(page));
  }
  const query = params.toString();
  window.history.replaceState(
    null,
    "",
    `${window.location.pathname}${query ? `?${query}` : ""}`,
  );
}

// ── Trigger label helper ──────────────────────────────────────────────────────

export function getIntentLabel(intent: string): string {
  const labels: Record<string, string> = {
    release: "Release",
    deploy: "Deploy",
    push_to_main: "Push to main",
  };
  return labels[intent] ?? intent;
}
